import { useQueries, useQuery } from '@tanstack/react-query'
import { sortStringsAlphabetically } from '../../../../helpers/sorting'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { userQueries } from '../../../../api/users'
import { getUserFullName } from '../../../../helpers/user'

export const useSortedAdminsList = (): {
  sortedAdminList: {
    user: IUserModel & { fullName: string }
    role: string
  }[]
  isLoading: boolean
} => {
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()

  const adminsQuery = useQueries({
    queries:
      society?.adminsList?.map((admin) => {
        return {
          ...userQueries.getUser(admin.userId),
          select: (user: IUserModel) => {
            return { ...user, fullName: getUserFullName(user) }
          },
        }
      }) || [],
  })

  const { data: masterAdminUser } = useQuery(
    userQueries.getUser(society?.masterAdminUserId)
  )
  const admins = adminsQuery.flatMap((query) => query.data || [])
  const isLoading = adminsQuery.flatMap((query) => query.isLoading || [])

  const sortedAdminList = (): {
    user: IUserModel & { fullName: string }
    role: string
  }[] => {
    const nonMasterAdmins = admins
      .filter((admin) => admin._id !== society?.masterAdminUserId)
      .sort((a, b) => sortStringsAlphabetically(a.fullName, b.fullName))
    return [
      ...(masterAdminUser
        ? [
            {
              user: {
                ...masterAdminUser,
                fullName: getUserFullName(masterAdminUser),
              },
              role: translate('common.user.masterAdmin'),
            },
          ]
        : []),
      ...nonMasterAdmins.map((user) => ({
        user,
        role: translate('common.user.admin'),
      })),
    ]
  }

  return { sortedAdminList: sortedAdminList(), isLoading: !!isLoading.length }
}
